import { Flex, Image, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Package } from '@phosphor-icons/react';
import type { WmsShipmentInboundType } from 'schemas/shipment/inbound/detail';
import { COLOR } from '@styles/theme';
import { CommonUtility } from '@utility/common';
import {
  getShipmentItemStatusMeta,
  resolveAliasLineStatus,
} from '@utility/utils/shipment-display';
import { InboundDetailContentsTableWrap } from './inboundDetailStyled';

const { Text } = Typography;

type LineRow = {
  key: string;
  itemId: string;
  productName: string;
  imageUrl?: string;
  sku: string;
  upc: string;
  alias: string;
  cartonCount: number;
  unitCount: number;
  countedCartons: number;
  countedUnits: number;
  length: number;
  width: number;
  height: number;
  lengthUnit: string;
  unitsPerCarton: number;
  grossWeight: number;
  grossWeightUnit: string;
  lineStatus: string;
};

function CopyableField({ label, value }: { label: string; value: string }) {
  return (
    <Flex align="center" gap={8} wrap="wrap">
      <Text type="secondary" style={{ fontSize: 12, minWidth: 28 }}>
        {label}
      </Text>
      <Typography.Text copyable={{ text: value }} style={{ fontSize: 14, margin: 0 }}>
        {value}
      </Typography.Text>
    </Flex>
  );
}

function isUnitsQtyType(qtyType?: string) {
  const normalized = (qtyType ?? '').trim().toLowerCase();
  return normalized === 'units' || normalized === 'unit';
}

function formatUnitsLine(unitCount: number) {
  const label = unitCount === 1 ? 'Unit' : 'Units';
  return (
    <Text strong style={{ fontSize: 14 }}>
      {CommonUtility.decimalWithCommas(CommonUtility.roundNumber(unitCount))} {label}
    </Text>
  );
}

function formatCartonLine(cartonCount: number, unitCount: number) {
  const cartonLabel = cartonCount === 1 ? 'carton' : 'cartons';
  return (
    <>
      <Text strong style={{ fontSize: 14 }}>
        {CommonUtility.decimalWithCommas(CommonUtility.roundNumber(cartonCount))}{' '}
        {cartonLabel}
      </Text>
      <Text type="secondary" style={{ fontSize: 14 }}>
        {' '}
        ({CommonUtility.decimalWithCommas(CommonUtility.roundNumber(unitCount))} Units)
      </Text>
    </>
  );
}

function formatQuantityLine(
  cartonCount: number,
  unitCount: number,
  isUnitsMode: boolean,
) {
  return isUnitsMode
    ? formatUnitsLine(unitCount)
    : formatCartonLine(cartonCount, unitCount);
}

function CartonSpecBadges({ row }: { row: LineRow }) {
  const badgeStyle = {
    margin: 0,
    background: COLOR.box,
    borderColor: COLOR.border,
    color: COLOR.black,
    fontSize: 12,
    lineHeight: '20px',
  };

  return (
    <Flex gap={8} wrap="wrap">
      <Tag variant="outlined" style={badgeStyle}>
        {CommonUtility.decimalWithCommas(row.length)} x{' '}
        {CommonUtility.decimalWithCommas(row.width)} x{' '}
        {CommonUtility.decimalWithCommas(row.height)} {row.lengthUnit}
      </Tag>
      <Tag variant="outlined" style={{ ...badgeStyle, color: COLOR.textSecondary }}>
        {CommonUtility.decimalWithCommas(row.unitsPerCarton)} units
      </Tag>
      <Tag variant="outlined" style={{ ...badgeStyle, color: COLOR.textSecondary }}>
        Gross {CommonUtility.decimalWithCommas(row.grossWeight)} {row.grossWeightUnit}
      </Tag>
    </Flex>
  );
}

function ProductCell({ row, isUnitsMode }: { row: LineRow; isUnitsMode: boolean }) {
  return (
    <Flex gap={12} align="flex-start">
      {row.imageUrl ? (
        <Image
          src={row.imageUrl}
          alt=""
          width={48}
          height={48}
          style={{ objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
          preview={false}
        />
      ) : (
        <Flex
          align="center"
          justify="center"
          style={{
            width: 48,
            height: 48,
            borderRadius: 4,
            background: COLOR.box,
            flexShrink: 0,
          }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            —
          </Text>
        </Flex>
      )}
      <Flex vertical gap={8} style={{ minWidth: 0 }}>
        <Text strong style={{ fontSize: 14, lineHeight: '20px' }}>
          {row.productName}
        </Text>
        {!isUnitsMode ? (
          <>
            <Flex align="center" gap={6}>
              <Package size={14} color={COLOR.textSecondary} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.alias ? `Alias ${row.alias}` : 'Carton Specs'}
              </Text>
            </Flex>
            <CartonSpecBadges row={row} />
          </>
        ) : null}
      </Flex>
    </Flex>
  );
}

function flattenItems(data: WmsShipmentInboundType): LineRow[] {
  const rows: LineRow[] = [];

  (data.items ?? []).forEach(item => {
    if (!item.aliases?.length) {
      rows.push({
        key: item.itemId,
        itemId: item.itemId,
        productName: item.title || '—',
        imageUrl: item.images?.display_image,
        sku: item.sku || '—',
        upc: item.upc || '—',
        alias: '',
        cartonCount: 0,
        unitCount: item.receivedQty ?? 0,
        countedCartons: 0,
        countedUnits: item.receivedQty ?? 0,
        length: 0,
        width: 0,
        height: 0,
        lengthUnit: 'cm',
        unitsPerCarton: 1,
        grossWeight: 0,
        grossWeightUnit: 'kg',
        lineStatus: 'pending',
      });
      return;
    }

    item.aliases.forEach(alias => {
      const attrs = alias.attributes ?? {};
      const unitsPerCarton = attrs.units_per_carton || 1;
      const cartonCount = alias.qty / unitsPerCarton || 0;
      const counted =
        alias.metadata?.checked_quantity ?? alias.counted ?? alias.received_qty ?? 0;
      const countedCartons = counted / unitsPerCarton || 0;

      rows.push({
        key: `${item.itemId}-${alias._id}`,
        itemId: item.itemId,
        productName: item.title || '—',
        imageUrl: item.images?.display_image,
        sku: item.sku || '—',
        upc: item.upc || '—',
        alias: alias.alias,
        cartonCount,
        unitCount: alias.qty,
        countedCartons,
        countedUnits: counted,
        length: attrs.carton_length?.value ?? 0,
        width: attrs.carton_width?.value ?? 0,
        height: attrs.carton_height?.value ?? 0,
        lengthUnit: attrs.carton_length?.unit || 'cm',
        unitsPerCarton,
        grossWeight: attrs.carton_gross_weight?.value ?? 0,
        grossWeightUnit: attrs.carton_gross_weight?.unit || 'kg',
        lineStatus: resolveAliasLineStatus(alias, cartonCount),
      });
    });
  });

  return rows;
}

type InboundShipmentContentsTableProps = {
  data: WmsShipmentInboundType;
};

export function InboundShipmentContentsTable({
  data,
}: InboundShipmentContentsTableProps) {
  const rows = flattenItems(data);
  const isUnitsMode = isUnitsQtyType(data.qtyType);

  const columns: ColumnsType<LineRow> = [
    {
      key: 'product',
      title: 'Product',
      render: (_, record) => <ProductCell row={record} isUnitsMode={isUnitsMode} />,
    },
    {
      key: 'config',
      title: 'Config',
      width: 260,
      render: (_, record) => (
        <Flex vertical gap={10}>
          <CopyableField label="SKU" value={record.sku} />
          <CopyableField label="UPC" value={record.upc} />
        </Flex>
      ),
    },
    {
      key: 'quantity',
      title: 'Quantity',
      width: 200,
      render: (_, record) => (
        <Flex vertical align="flex-start" gap={6}>
          <div>
            {formatQuantityLine(record.cartonCount, record.unitCount, isUnitsMode)}
          </div>
          <Flex align="flex-start" wrap="wrap">
            <Text style={{ fontSize: 14 }}>Counted: </Text>
            {formatQuantityLine(record.countedCartons, record.countedUnits, isUnitsMode)}
          </Flex>
        </Flex>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      width: 100,
      render: (_, record) => {
        const meta = getShipmentItemStatusMeta(record.lineStatus);
        return (
          <Tag color={meta.color} style={{ margin: 0 }}>
            {meta.label}
          </Tag>
        );
      },
    },
  ];

  return (
    <InboundDetailContentsTableWrap>
      <Table<LineRow>
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="middle"
        style={{ border: 'none' }}
      />
    </InboundDetailContentsTableWrap>
  );
}
